// client/src/pages/seo/QrCodeMenuWithoutAppEn.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";

const sections = [
  { id: "answer", label: "How does a QR menu without an app work?" },
  { id: "scanner", label: "Do guests need a separate QR scanner?" },
  { id: "browser", label: "Browser menu or restaurant app?" },
  { id: "requirements", label: "What should a good browser-based menu be able to do?" },
  { id: "mistakes", label: "Common mistakes" },
  { id: "faq", label: "FAQ" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function QrCodeMenuWithoutAppEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title = "QR Code Menu Without App: Open Menu in Browser | Best Place";
    setMeta(
      "description",
      "QR code menu without an app: guests scan the code with their smartphone and open the menu directly in the browser — no download, login or registration."
    );
    setCanonical("https://best-place.club/en/qr-code-menu-without-app");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          de: "/de/qr-code-speisekarte-ohne-app",
          en: "/en/qr-code-menu-without-app",
          no: "/no/qr-kode-meny-uten-app",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>QR Code Menu Without an App</span>
          </h1>
          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Open the camera, scan the QR code, tap the link and view the menu in
                the browser. A guest should not have to do more than that just to
                open a menu.
              </p>
              <p className="seoHero__text">
                This page deliberately covers only one question: how a QR menu works
                without an app. For choosing, creating and using a complete QR menu,
                see our main page.
              </p>
              <p>
                <Link to="/en/qr-code-menu-for-restaurants">Go to QR code menus for restaurants</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="articls">
        <h2>Contents</h2>
        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button type="button" className="qrArticle__navButton" onClick={() => scrollToSection(item.id)}>
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="answer" className="acquaintance">
          <h2>How does a QR menu without an app work?</h2>
          <p>
            The QR code contains or points to a normal web link. Modern smartphones
            recognize this link after scanning and offer it directly for opening.
            The menu then loads like a mobile website in the browser.
          </p>
          <ol className="qrArticle__block-li">
            <li className="text__seo-li">Open the smartphone camera.</li>
            <li className="text__seo-li">Scan the QR code on the table.</li>
            <li className="text__seo-li">Tap the displayed link.</li>
            <li className="text__seo-li">View the menu directly in the browser.</li>
          </ol>
          <p>
            Guests do not need the App Store, Google Play, account creation or login.
          </p>
        </section>

        <section id="scanner" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Do guests need a separate QR scanner?</h2>
            <p>
              Usually not. On current iPhones and Android devices, the built-in
              camera can recognize QR codes. What matters more than a separate
              scanner app is a clearly printed code and an accessible destination page.
            </p>
          </div>
        </section>

        <section id="browser" className="acquaintance">
          <h2>Browser menu or restaurant app?</h2>
          <div className="seoComparison">
            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Browser menu</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">available immediately</li>
                <li className="text__seo-li">no download</li>
                <li className="text__seo-li">good for first-time guests</li>
                <li className="text__seo-li">simple access at the table</li>
              </ul>
            </div>
            <div className="seoComparison__col">
              <h3>Dedicated app</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">can make sense for regular customers</li>
                <li className="text__seo-li">useful for loyalty or complex features</li>
                <li className="text__seo-li">requires an additional installation</li>
                <li className="text__seo-li">often unnecessary just to view a menu</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="requirements" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should a good browser-based menu be able to do?</h2>
            <ul className="qrArticle__block-li">
              <li className="text__seo-li">load quickly on a smartphone</li>
              <li className="text__seo-li">show clear categories</li>
              <li className="text__seo-li">display prices clearly without zooming</li>
              <li className="text__seo-li">use photos only where they help</li>
              <li className="text__seo-li">support multiple languages when needed</li>
              <li className="text__seo-li">open without login or registration</li>
            </ul>
            <p>
              The browser question is only one part of the product decision. If you
              are looking for a complete solution, read{" "}
              <Link to="/en/qr-code-menu-for-restaurants">QR code menu for restaurants</Link>.
            </p>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Common mistakes with no-app QR menus</h2>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">The QR code opens only a hard-to-read PDF file.</li>
            <li className="text__seo-li">The mobile page loads slowly or uses text that is too small.</li>
            <li className="text__seo-li">Registration is still required after scanning.</li>
            <li className="text__seo-li">The QR code was not tested on several devices before printing.</li>
            <li className="text__seo-li">There is no explanation next to the code of what opens after scanning.</li>
          </ul>
        </section>

        <section id="faq" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>QR code menu without app FAQ</h2>
            <div className="seoFaq">
              <details>
                <summary>Does it work on iPhone?</summary>
                <p>Yes. The QR code can be recognized by the camera and the link can be opened in the browser.</p>
              </details>
              <details>
                <summary>Does it work on Android?</summary>
                <p>Yes. Current Android devices usually recognize QR codes through the camera or Google Lens.</p>
              </details>
              <details>
                <summary>Can a PDF also be opened without an app?</summary>
                <p>Yes, but a PDF is not automatically mobile-friendly. A real mobile menu page is often easier to read.</p>
              </details>
              <details>
                <summary>Do I need to develop a dedicated restaurant app?</summary>
                <p>Usually not if the purpose is simply to display the menu at the table.</p>
              </details>
            </div>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Related topics</h2>
          <div className="seoCompareSmall">
            <p><Link to="/en/qr-code-menu-for-restaurants">QR code menu for restaurants</Link></p>
            <p><Link to="/en/qr-code-menu-printing">Print a QR code</Link></p>
            <p><Link to="/en/digital-menu-for-restaurants">Digital menu</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}
